import React, { useState, useEffect, memo, useRef } from 'react';
import classnames from 'classnames';
import { useDrag } from 'react-dnd';
import { Icons } from '../Icons';
import { DisplaySetMessageListTooltip } from '../DisplaySetMessageListTooltip';
import { TooltipTrigger, TooltipContent, Tooltip } from '../Tooltip';
import ProgressLoadingBar from '../ProgressLoadingBar';
import { useThumbnailLocationListener } from '@state';
import { useViewerLayoutContext } from '../../contextProviders';

const DraggableThumbnailContent = ({ dragData, children }: any) => {
  const [, drag] = useDrag({
    type: 'displayset',
    item: { ...dragData },
    canDrag: function () {
      return Object.keys(dragData).length !== 0;
    },
  });

  return (
    <div
      ref={drag}
      className="h-full w-full"
    >
      {children}
    </div>
  );
};

const StaticThumbnailContent = ({ children }: any) => {
  return <div className="h-full w-full">{children}</div>;
};

/**
 * Display a thumbnail for a display set.
 */
const Thumbnail = memo(
  ({
    servicesManager,
    displaySetInstanceUID,
    className,
    children,
    imageSrc,
    imageAltText,
    description,
    seriesNumber,
    numInstances,
    details,
    loadingProgress,
    countIcon,
    messages,
    isActive,
    onClick,
    onDoubleClick,
    thumbnailType,
    modality,
    viewPreset = 'thumbnails',
    isHydratedForDerivedDisplaySet = false,
    isTracked = false,
    canReject = false,
    dragData = {},
    isDraggable = true,
    onReject = () => {},
    onClickUntrack = () => {},
    ThumbnailMenuItems = () => {},
    onLoadComplete = () => {},
    onImageLoadError = () => {},
  }: withAppTypes): React.ReactNode => {
    const studyPrefetcherService = servicesManager?.services?.studyPrefetcherService;
    const [currentLoadingProgress, setCurrentLoadingProgress] = useState(
      () =>
        studyPrefetcherService?.getDisplaySetLoadingProgress(displaySetInstanceUID) ??
        loadingProgress
    );
    const [currentLoadingStatus, setCurrentLoadingStatus] = useState<string>();
    const [failedImageCount, setFailedImageCount] = useState(0);
    const thumbnailLocation = useThumbnailLocationListener();
    const { isPatientMobile } = useViewerLayoutContext();
    const touchStartRef = useRef({ x: 0, y: 0 });
    const lastTapRef = useRef(0);
    const [imageLoadFailed, setImageLoadFailed] = useState(false);

    useEffect(() => {
      setCurrentLoadingProgress(
        studyPrefetcherService?.getDisplaySetLoadingProgress(displaySetInstanceUID) ??
          loadingProgress
      );
      if (!studyPrefetcherService) {
        return;
      }

      const { unsubscribe } = studyPrefetcherService.subscribe(
        studyPrefetcherService.EVENTS.DISPLAYSET_LOAD_PROGRESS,
        updatedDisplaySetLoadingState => {
          if (updatedDisplaySetLoadingState.displaySetInstanceUID !== displaySetInstanceUID) {
            return;
          }

          const nextLoadingProgress = updatedDisplaySetLoadingState.loadingProgress;
          setCurrentLoadingProgress(nextLoadingProgress);
          setCurrentLoadingStatus(updatedDisplaySetLoadingState.status);
          setFailedImageCount(updatedDisplaySetLoadingState.failedCount || 0);
          if (nextLoadingProgress === 1) {
            onLoadComplete && onLoadComplete(); // 当加载完成时调用回调函数
          }
        }
      );
      return () => unsubscribe();
    }, [displaySetInstanceUID, loadingProgress, onLoadComplete, studyPrefetcherService]);

    useEffect(() => {
      setImageLoadFailed(false);
    }, [imageSrc]);

    const shouldRenderThumbnailImage = Boolean(imageSrc && !imageLoadFailed);

    const loadingStateMessage =
      currentLoadingStatus === 'failed'
        ? failedImageCount > 1
          ? `${failedImageCount} 张加载失败`
          : '图像加载失败'
        : currentLoadingStatus === 'not-cached'
          ? currentLoadingProgress && currentLoadingProgress > 0
            ? '部分图像需重载'
            : '打开时加载'
          : undefined;
    /**
     * The detail line under the description. `details` comes from the
     * `studyBrowser.thumbnailDetails` customization, resolved by the panel; the
     * series number and instance count below are the same two items the default
     * customization declares, kept here so the component still stands alone.
     */
    const renderDetails = (textClass: string, firstItemClass?: string) => {
      // `??`, not `|| `: an unset `details` means no customization was resolved
      // for this thumbnail and the defaults below stand in, whereas an empty
      // `details` is a customization that resolved to no items at all and is
      // honoured as the empty line it asks for.
      const items = details ?? [
        { id: 'SeriesNumber', label: 'S:', value: seriesNumber },
        { id: 'InstanceCount', iconName: countIcon || 'InfoSeries', value: numInstances },
      ];

      return (
        <div className="flex h-[12px] items-center gap-[7px] overflow-hidden">
          {items.map(({ id, label, title, value, iconName }, index) => (
            <div
              key={id ?? index}
              className={classnames(
                'text-muted-foreground',
                textClass,
                index === 0 && firstItemClass
              )}
              title={title || undefined}
              data-cy={`thumbnail-detail-${id}`}
            >
              <div className="flex items-center gap-[4px]">
                {iconName &&
                  React.createElement(Icons[iconName] || Icons.MissingIcon, { className: 'w-3' })}
                <div>
                  {label}
                  {value}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    };

    const handleTouchStart = (e: React.TouchEvent) => {
      const touch = e.touches[0];

      if (!touch) {
        return;
      }

      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
      const touch = e.changedTouches[0];

      if (!touch) {
        return;
      }

      const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

      // 如果滑动超过 10px，则视为滑动行为，忽略点击
      if (deltaX > 10 || deltaY > 10) {
        return;
      }

      if (e.cancelable) {
        e.preventDefault();
      }

      const currentTime = new Date().getTime();
      const tapLength = currentTime - lastTapRef.current;
      if (tapLength < 300 && tapLength > 0) {
        onDoubleClick(e);
      } else {
        if (isPatientMobile) {
          onDoubleClick(e);
        } else {
          onClick(e);
        }
      }
      lastTapRef.current = currentTime;
    };

    const renderThumbnailPreset = () => {
      return (
        <div
          className={classnames(
            'flex h-full w-full flex-col items-center justify-center gap-[1px] p-[1px]',
            isActive ? 'border border-[#e51b48]' : 'border border-[#515157]'
          )}
        >
          <div className="h-[114px] w-[128px]">
            <div className="relative bg-black">
              {shouldRenderThumbnailImage ? (
                <img
                  src={imageSrc}
                  alt={imageAltText}
                  className="h-[114px] w-[128px] rounded object-contain"
                  crossOrigin="anonymous"
                  onError={() => {
                    setImageLoadFailed(true);
                    onImageLoadError();
                  }}
                />
              ) : (
                <div className="bg-background h-[114px] w-[128px] rounded">{children}</div>
              )}

              {/* bottom */}
              <div className="absolute bottom-0 left-0 h-[2px] w-[128px]">
                {currentLoadingProgress !== undefined && (
                  <ProgressLoadingBar
                    progress={
                      currentLoadingProgress >= 1 ? 100 : Math.floor(currentLoadingProgress * 100)
                    }
                  />
                )}
              </div>

              {isPatientMobile && loadingStateMessage && (
                <div
                  className={classnames(
                    'absolute bottom-[5px] left-[4px] rounded px-1 py-0.5 text-[10px] leading-3 text-white',
                    currentLoadingStatus === 'failed' ? 'bg-red-700/90' : 'bg-black/70'
                  )}
                  title={loadingStateMessage}
                >
                  {loadingStateMessage}
                </div>
              )}

              {details !== undefined ? (
                <div className="absolute top-0 left-0 right-0 bg-black/50">
                  {renderDetails('text-[11px]', 'pl-1')}
                </div>
              ) : (
                <>
                  {/* top left */}
                  <div
                    className="absolute top-0 left-0 flex h-[14px] items-center gap-[1px] p-[4px]"
                    style={{
                      backgroundColor: 'rgba(128, 128, 128, 0.5)', // 半透明背景
                      color: '#fff', // 白色字体
                    }}
                  >
                    <div className="text-[11px]">S:{seriesNumber}</div>
                  </div>
                  {/* top right */}
                  <div
                    className="absolute top-0 right-0 flex h-[14px] items-center gap-[1px] p-[4px]"
                    style={{
                      backgroundColor: 'rgba(128, 128, 128, 0.5)', // 半透明背景
                      color: '#fff', // 白色字体
                    }}
                  >
                    {' '}
                    {countIcon ? (
                      React.createElement(Icons[countIcon] || Icons.MissingIcon, {
                        className: 'w-3',
                      })
                    ) : (
                      <Icons.InfoSeries className="w-3" />
                    )}
                    <div className="text-[11px]">{numInstances}</div>
                  </div>
                </>
              )}

              {/* bottom right */}
              <div className="absolute bottom-0 right-0 flex items-center gap-[4px] p-[4px]">
                {/* <DisplaySetMessageListTooltip
                  messages={messages}
                  id={`display-set-tooltip-${displaySetInstanceUID}`}
                /> */}
                <ThumbnailMenuItems
                  displaySetInstanceUID={displaySetInstanceUID}
                  canReject={canReject}
                  onReject={onReject}
                />
              </div>
            </div>
          </div>
          <div className="flex h-[22px] w-[128px] flex-col justify-end">
            <Tooltip>
              <TooltipContent>{description}</TooltipContent>
              <TooltipTrigger>
                <div className="min-h-[20px] w-[128px] overflow-hidden text-ellipsis whitespace-nowrap pb-0.5 pl-1 text-left text-[12px] font-normal leading-4 text-white">
                  {description}
                </div>
              </TooltipTrigger>
            </Tooltip>
          </div>
        </div>
      );
    };

    const renderListPreset = () => {
      return (
        <div
          className={classnames(
            'flex h-full w-full items-center justify-between pr-[8px] pl-[8px] pt-[4px] pb-[4px]',
            isActive ? 'border border-[#e51b48]' : 'border border-[#515157]'
          )}
        >
          <div className="relative flex h-[32px] w-full items-center gap-[8px] overflow-hidden">
            <div
              className={classnames(
                'h-[32px] w-[4px] min-w-[4px] rounded',
                isActive || isHydratedForDerivedDisplaySet ? 'bg-highlight' : 'bg-primary/65',
                currentLoadingProgress && currentLoadingProgress < 1 && 'bg-[rgba(27,182,229,0.65)]'
              )}
            ></div>
            <div className="flex h-full w-[calc(100%-12px)] flex-col justify-start">
              <div className="flex items-center gap-[7px]">
                <div className="text-[13px] font-semibold text-white">{modality}</div>
                <Tooltip>
                  <TooltipContent>{description}</TooltipContent>
                  <TooltipTrigger className="w-full overflow-hidden">
                    <div className="max-w-[160px] overflow-hidden overflow-ellipsis whitespace-nowrap text-left text-[13px] font-normal text-white">
                      {description}
                    </div>
                  </TooltipTrigger>
                </Tooltip>
              </div>

              {renderDetails('text-[12px]')}
            </div>
          </div>
          <div className="flex h-full items-center gap-[4px]">
            <DisplaySetMessageListTooltip
              messages={messages}
              id={`display-set-tooltip-${displaySetInstanceUID}`}
            />
            {isTracked && (
              <Tooltip>
                <TooltipTrigger>
                  <div className="group">
                    <Icons.StatusTracking className="text-primary-light h-[20px] w-[15px] group-hover:hidden" />
                    <Icons.Cancel
                      className="text-primary-light hidden h-[15px] w-[15px] group-hover:block"
                      onClick={onClickUntrack}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <div className="flex flex-1 flex-row">
                    <div className="flex-2 flex items-center justify-center pr-4">
                      <Icons.InfoLink className="text-primary" />
                    </div>
                    <div className="flex flex-1 flex-col">
                      <span>
                        <span className="text-white">
                          {isTracked ? 'Series is tracked' : 'Series is untracked'}
                        </span>
                      </span>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
            <ThumbnailMenuItems
              displaySetInstanceUID={displaySetInstanceUID}
              canReject={canReject}
              onReject={onReject}
            />
          </div>
        </div>
      );
    };

    return (
      <div
        className={classnames(
          className,
          'group flex select-none flex-col rounded bg-[#323237] outline-none',
          isDraggable && 'cursor-pointer hover:bg-[#323237]/30',
          !isDraggable && 'cursor-default',
          viewPreset === 'thumbnails' ? 'h-[140px] w-[135px]' : '',
          thumbnailLocation === 'top' ? 'h-[140px] w-[135px]' : '',
          viewPreset === 'list' ? 'h-[40px] w-full' : ''
        )}
        id={`thumbnail-${displaySetInstanceUID}`}
        data-cy={
          thumbnailType === 'thumbnailNoImage'
            ? 'study-browser-thumbnail-no-image'
            : 'study-browser-thumbnail'
        }
        data-series={seriesNumber}
        onClick={isDraggable ? onClick : undefined}
        onDoubleClick={isDraggable ? onDoubleClick : undefined}
        onTouchStart={isDraggable ? handleTouchStart : undefined}
        onTouchEnd={isDraggable ? handleTouchEnd : undefined}
        role={isDraggable ? 'button' : undefined}
      >
        {isDraggable ? (
          <DraggableThumbnailContent dragData={dragData}>
            {viewPreset === 'thumbnails' && renderThumbnailPreset()}
            {viewPreset === 'list' && renderListPreset()}
          </DraggableThumbnailContent>
        ) : (
          <StaticThumbnailContent>
            {viewPreset === 'thumbnails' && renderThumbnailPreset()}
            {viewPreset === 'list' && renderListPreset()}
          </StaticThumbnailContent>
        )}
      </div>
    );
  }
);

export { Thumbnail };
